import type { WorkerBridgeCallout } from '../types.js'

/** How long a settled callout (yours, covered, expired, withdrawn) stays on the Home card after it settled. */
export const SETTLED_SHOWN_MS = 24 * 60 * 60 * 1000

/** When a callout stopped being open, as the worker's view carries it: the fill, else the expiry. */
function settledAt(callout: WorkerBridgeCallout): number {
  return Date.parse(callout.filledAt ?? callout.expiresAt)
}

/**
 * What the Home card shows (plan section 7): every callout still open to
 * answer, then the ones that settled in the last day - the shift that is
 * now theirs (kept until it ends), the ones covered by someone else, the
 * ones that closed or were withdrawn - so a worker who taps the alert late
 * still learns what happened. Older history is the Shifts calendar's.
 */
export function cardCallouts(callouts: readonly WorkerBridgeCallout[], now: Date): { open: WorkerBridgeCallout[]; settled: WorkerBridgeCallout[] } {
  const nowMs = now.getTime()
  const open = callouts.filter((callout) => callout.result === 'open')
  const settled = callouts.filter((callout) => {
    if (callout.result === 'open') {
      return false
    }
    if (callout.result === 'yours') {
      return Date.parse(callout.shiftEndsAt) > nowMs
    }
    return nowMs - settledAt(callout) < SETTLED_SHOWN_MS
  })
  return { open, settled }
}

/** "Pays USD 20.00/hr plus 2.00/hr Bridge premium", or null when the facility showed no pay. */
export function describePay(callout: Pick<WorkerBridgeCallout, 'pay'>): string | null {
  if (!callout.pay) {
    return null
  }
  const premium = callout.pay.premiumPerHour ? ` plus ${callout.pay.premiumPerHour.toFixed(2)}/hr Bridge premium` : ''
  return `Pays ${callout.pay.currency} ${callout.pay.rate.toFixed(2)}/hr${premium}`
}

/** How a settled callout reads to the worker, in one line. */
export function describeResult(callout: WorkerBridgeCallout): string {
  switch (callout.result) {
    case 'yours':
      return `This shift is yours. Be at the facility within ${callout.arrivalWindowMinutes} minutes of when you accepted.`
    case 'covered':
      return callout.myAnswer?.answer === 'decline' ? 'You declined; it was covered by someone else.' : 'Covered by someone else. Thanks for being enrolled.'
    case 'expired':
      return callout.myAnswer?.answer === 'decline' ? 'You declined; nobody else took it in time.' : 'Nobody accepted in time, so this callout closed.'
    case 'withdrawn':
      return 'The facility withdrew this callout.'
    case 'open':
      return 'Open.'
  }
}

/** The callout a Bridge message is about, from its dedupe key (`bridge_<kind>:<calloutId>`, as the API writes it); null for any other message. */
export function calloutIdOfMessage(message: { dedupeKey?: string | null }): string | null {
  const match = /^bridge_(?:opened|cancelled|filled|covered):(.+)$/.exec(message.dedupeKey ?? '')
  return match?.[1] ?? null
}
